import { Box, Divider, Text, ScrollArea, Center, Stack } from '@mantine/core';

import { useParams } from 'react-router-dom';

import { useCallback } from 'react';

import CenteredLoader from '../../CenteredLoader';
import ModalContent from '../../ModalContent';
import useEnsisMutation from '../../../hooks/useEnsisMutation';

import { type ProposalSection, type WithoutPermissions } from '../../../types/apiTypes';

interface Props {
  loading: boolean
  outline: string[]
  onClose: () => void
  onSetOutlineSuccess: (data: Array<WithoutPermissions<ProposalSection>>) => void
}
const NEW_OUTLINE_SUBTITLE = 'Selecting this outline will replace your current one.' +
  'Any previously sorted requirements will need to be re-sorted.';
const ExtractOutline: React.FC<Props> = (props: Props) => {
  const { loading, outline, onClose, onSetOutlineSuccess } = props;
  const { proposalUid } = useParams();

  const setOutlineMutation = useEnsisMutation(`/app/proposals/${proposalUid}/set-outline`, {
    requestType: 'patch',
    queryKeysToInvalidate: [`/app/proposals/${proposalUid}/data`, '/app/requirement-responses'],
    showSuccessMessage: false,
    onSuccess: (data) => {
      onSetOutlineSuccess(data);
      onClose();
    }
  });

  const handleReplaceOutline = useCallback(() => {
    setOutlineMutation.mutate({ outline });
  }, [outline, setOutlineMutation]);

  if (loading) {
    return <Stack justify='center' h={300} align='center'>
      <CenteredLoader loaderProps={{ size: 100 }} />
      <Text>
        Extracting outline...
      </Text>
    </Stack>;
  }

  if (outline.length === 0) {
    return (
      <ModalContent
        title='No Outline Found'
        onClose={onClose}
        primaryButton={{ label: 'Okay', onClick: onClose }}
      >
        <Center flex={1} h={300}>
          <Stack gap={0}>
            <Text ta='center'>
              No outline was found in your opportunity.
            </Text>
            <Text ta='center'>
              You can rename, delete, or add sections manually.
            </Text>
          </Stack>
        </Center>
      </ModalContent>);
  };

  return (
    <ModalContent
      title='Review New Outline'
      subtitle={NEW_OUTLINE_SUBTITLE}
      onClose={onClose}
      primaryButton={{ label: 'Replace Outline', onClick: handleReplaceOutline }}
      secondaryButton={{ label: 'Cancel', onClick: onClose }}
    >
      <ScrollArea.Autosize mah={450}>
        {outline?.map((sectionTitle, index) => <>
          <Box display='flex' style={{ alignItems: 'center' }} h={60}>
            <Text fz='sm' fw={500} key={+index} >
              {sectionTitle}
            </Text>
          </Box>
          <Divider />
        </>)}
      </ScrollArea.Autosize >
    </ModalContent>);
};

export default ExtractOutline;
